/**
 * RÜCKFRAGEN ALS FENSTER DER SEITE, NICHT DES BROWSERS (.562).
 * ============================================================================
 *
 * ORIGINALTEXT MARVIN (30.10.2026)
 *   „gerade was bei den Terminierungen und so weiter sind, wieder diese, sage
 *    ich mal, browserspezifischen Pop-ups kommen, um irgendwas zu bestätigen,
 *    ja, senden, pushen, senden, ja, nein, ich will, dass das modal auf der
 *    normalen Seite als Pop-up richtig erscheint und nicht so als, ja, es sieht
 *    aus wie ein Pop-up von einem Browser. Prüft das überall auf der Seite und
 *    baue das um."
 *
 * Ersetzt `window.confirm`, `window.prompt` und `window.alert` überall: das
 * Fenster ist der `Dialog` der Plattform (Rahmen, Escape, Fensterstapel), die
 * Knöpfe tragen Worte statt „OK/Abbrechen". Steht im Text die alte Zeile
 * „OK = ja, pushen\nAbbrechen = nein, nicht pushen", werden daraus die
 * Aufschriften der beiden Knöpfe.
 */
import { useState, type ReactElement } from 'react';
import { createRoot } from 'react-dom/client';
import { Dialog } from './Formular.tsx';

interface Optionen { titel?: string; ja?: string; nein?: string; gefaehrlich?: boolean; /** Eingabe verdeckt (Passwort, Schlüssel). */ geheim?: boolean }

/** Die Knopfworte aus der alten „OK = … / Abbrechen = …"-Zeile holen. */
function zerlegen(text: string, o: Optionen): { text: string; ja: string; nein: string } {
  let t = text; let ja = o.ja ?? ''; let nein = o.nein ?? '';
  const m = /\n*\s*OK = ([^\n·]+?)\s*(?:·|\n)\s*Abbrechen = ([^\n]+)\s*$/.exec(t);
  if (m) { t = t.slice(0, m.index).trimEnd(); ja = ja || m[1]!.trim(); nein = nein || m[2]!.trim(); }
  const gross = (w: string) => w ? w.charAt(0).toUpperCase() + w.slice(1) : w;
  return { text: t, ja: gross(ja) || 'Ja', nein: gross(nein) || 'Abbrechen' };
}

function oeffnen<T>(baue: (fertig: (wert: T) => void) => ReactElement): Promise<T> {
  return new Promise<T>((aufloesen) => {
    const ort = document.createElement('div');
    document.body.appendChild(ort);
    const wurzel = createRoot(ort);
    const fertig = (wert: T) => { aufloesen(wert); setTimeout(() => { wurzel.unmount(); ort.remove(); }, 0); };
    wurzel.render(baue(fertig));
  });
}

function Absaetze({ text }: { text: string }) {
  return <>{text.split(/\n{2,}/).map((a, i) => <p key={i} style={{ margin: '0 0 10px', whiteSpace: 'pre-line' }}>{a}</p>)}</>;
}

/** Ja oder Nein, als Fenster der Seite. Escape und Klick daneben heißen Nein. */
export function fragen(text: string, o: Optionen = {}): Promise<boolean> {
  const z = zerlegen(String(text ?? ''), o);
  return oeffnen<boolean>((fertig) => (
    <Dialog titel={o.titel ?? 'Bitte bestätigen'} breite={520} zu={() => fertig(false)}>
      <Absaetze text={z.text} />
      <div className="reihe" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 6 }}>
        <button type="button" className="knopf" onClick={() => fertig(false)}>{z.nein}</button>
        <button type="button" className={'knopf haupt' + (o.gefaehrlich ? ' gefahr' : '')} onClick={() => fertig(true)}>{z.ja}</button>
      </div>
    </Dialog>
  ));
}

/** Eine Eingabe, als Fenster der Seite. Abbrechen heißt null (wie `prompt`). */
export function eingeben(text: string, vorgabe = '', o: Optionen = {}): Promise<string | null> {
  function Feld({ fertig }: { fertig: (w: string | null) => void }) {
    const [w, setW] = useState(vorgabe ?? '');
    return (
      <Dialog titel={o.titel ?? 'Eingabe'} breite={520} zu={() => fertig(null)}>
        <Absaetze text={String(text ?? '')} />
        {o.geheim
          ? <input type="password" autoComplete="new-password" value={w} style={{ width: '100%' }} onChange={(e) => setW(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); fertig(w); } }} />
          : <textarea value={w} rows={3} style={{ width: '100%' }} onChange={(e) => setW(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); fertig(w); } }} />}
        <div className="reihe" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="knopf" onClick={() => fertig(null)}>{o.nein ?? 'Abbrechen'}</button>
          <button type="button" className="knopf haupt" onClick={() => fertig(w)}>{o.ja ?? 'Übernehmen'}</button>
        </div>
      </Dialog>
    );
  }
  return oeffnen<string | null>((fertig) => <Feld fertig={fertig} />);
}

/** Ein Hinweis mit einem Knopf, als Fenster der Seite. */
export function melden(text: string, o: Optionen = {}): Promise<void> {
  return oeffnen<void>((fertig) => (
    <Dialog titel={o.titel ?? 'Hinweis'} breite={520} zu={() => fertig()}>
      <Absaetze text={String(text ?? '')} />
      <div className="reihe" style={{ justifyContent: 'flex-end', marginTop: 6 }}>
        <button type="button" className="knopf haupt" onClick={() => fertig()}>{o.ja ?? 'OK'}</button>
      </div>
    </Dialog>
  ));
}
